import { useQuery } from '@tanstack/react-query'

import httpService from './httpService'
import type { ResolvedText } from './authoredText'

/**
 * Restrictions in force on a club, and the words to show about them.
 *
 * Two reads, because there are two audiences and they are told different things:
 * * a club's own staff read `GET /club/{id}/restrictions`, which answers with the staff wording —
 *   what the club has to do about it — and refuses anyone else;
 * * everyone else resolves the URNs on the club payload through `GET /restrictions/definitions`,
 *   which carries only public wording and only for the types that have any.
 *
 * The club payload never carries URNs the reader may not be told about, so a member simply sees a
 * shorter list than a director does — and a restriction a club has not disclosed publicly is
 * absent from theirs entirely.
 */
export interface ClubRestrictionBanner {
  urn: string
  severity: 'INFO' | 'WARNING' | 'CRITICAL'
  text: ResolvedText
  /** When it came into force; only the staff view knows. */
  since?: string | null
}

interface EnvelopeArray<T> {
  results?: T[]
}

/** The staff view: every restriction in force, in the wording the club's staff are shown. */
export const getClubStaffRestrictions = async (clubId: string | number) => {
  const res = await httpService.Get<EnvelopeArray<ClubRestrictionBanner>>(`/club/${clubId}/restrictions`)
  return res?.results ?? []
}

/** Public definitions for the given URNs. A URN with no public wording is simply absent. */
export const getRestrictionDefinitions = async (urns: string[]) => {
  if (urns.length === 0) return []
  const query = urns.map((urn) => `urns=${encodeURIComponent(urn)}`).join('&')
  return await httpService.Get<ClubRestrictionBanner[]>(`/restrictions/definitions?${query}`)
}

export const ClubRestrictionQueryKeys = {
  STAFF: 'club-restrictions-staff',
  DEFINITIONS: 'restriction-definitions',
}

/**
 * The banners to show this reader for this club.
 *
 * Definitions are cached for the whole session rather than per club: a type's wording is the same
 * wherever it is in force, which is why the server sends URNs on the club and the words separately.
 */
export const useClubRestrictionBanners = (clubId: string | number | undefined, urns: string[], isStaff: boolean) => {
  const staff = useQuery(
    [ClubRestrictionQueryKeys.STAFF, clubId],
    async () => await getClubStaffRestrictions(clubId as string | number),
    {
      enabled: !!clubId && isStaff,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    }
  )

  const sorted = [...urns].sort()
  const definitions = useQuery(
    [ClubRestrictionQueryKeys.DEFINITIONS, sorted.join(',')],
    async () => await getRestrictionDefinitions(sorted),
    {
      enabled: !isStaff && sorted.length > 0,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  )

  const banners = isStaff ? staff.data : definitions.data
  return {
    banners: banners ?? [],
    isLoading: isStaff ? staff.isLoading : definitions.isLoading,
  }
}
